import { Vector3 } from 'three';
import { EWall } from '../../@types/Enums';

import AttributeModel from '../../models/standard/AttributeModel';
import DoorModel from '../../models/standard/DoorModel';
import PassageModel from '../../models/standard/PassageModel';
import PartitionWallModel from '../../models/standard/PartitionWallModel';
import RadiatorModel from '../../models/standard/RadiatorModel';
import StandardWallModel from '../../models/standard/StandardWallModel';
import WindowModel from '../../models/standard/WindowModel';

export function instantiateWall(wallData: any): StandardWallModel | PartitionWallModel {
  const safeVec = (pt: any): Vector3 => new Vector3(pt?.x ?? 0, pt?.y ?? 0, pt?.z ?? 0);

  const toAttributes = (arr: any): AttributeModel[] =>
    Array.isArray(arr)
      ? arr.map(
          (a: any) =>
            new AttributeModel(
              a.id,
              a.key,
              a.value,
              a.predecessorId,
              a.type,
              a.unit,
              a.hidden,
              a.forced,
            ),
        )
      : [];

  const parseChildren = (src: any) => {
    const doors: DoorModel[] = Array.isArray(src?.doors)
      ? src.doors.map((doorData: any) => {
          const pos = doorData?.position ?? {};
          return new DoorModel(
            new Vector3(pos.x ?? 0, pos.y ?? 0, pos.z ?? 0),
            doorData?.length,
            doorData?.depth,
            doorData?.angle,
            doorData?.openingDirection,
            doorData?.orientation,
            toAttributes(doorData?.attributes),
            doorData?.uuid,
          );
        })
      : [];

    const windows: WindowModel[] = Array.isArray(src?.windows)
      ? src.windows.map((winData: any) => {
          const pos = winData?.position ?? {};
          return new WindowModel(
            new Vector3(pos.x ?? 0, pos.y ?? 0, pos.z ?? 0),
            winData?.length,
            winData?.depth,
            winData?.angle,
            toAttributes(winData?.attributes),
            winData?.uuid,
          );
      })
      : [];

    const passages: PassageModel[] = Array.isArray(src?.passages)
      ? src.passages.map((passageData: any) => {
          const pos = passageData?.position ?? {};
          return new PassageModel(
            new Vector3(pos.x ?? 0, pos.y ?? 0, pos.z ?? 0),
            passageData?.length,
            passageData?.depth,
            passageData?.angle,
            toAttributes(passageData?.attributes),
            passageData?.uuid,
          );
        })
      : [];

    const radiators: RadiatorModel[] = Array.isArray(src?.radiators)
      ? src.radiators.map((radData: any) => {
          const pos = radData?.position ?? {};
          return new RadiatorModel(
            new Vector3(pos.x ?? 0, pos.y ?? 0, pos.z ?? 0),
            radData?.length,
            radData?.depth,
            radData?.angle,
            toAttributes(radData?.attributes),
            radData?.uuid,
          );
        })
      : [];

    return { doors, windows, radiators, passages };
  };

  const start = safeVec(wallData?.startPoint);
  const end = safeVec(wallData?.endPoint);
  const depth = wallData?.depth ?? 0;
  const attributes = toAttributes(wallData?.attributes);
  const uuid: string | undefined = wallData?.uuid;

  if (wallData?.type === EWall.STANDARD) {
    const { doors, windows, radiators, passages } = parseChildren(wallData);

    const wall = new StandardWallModel(start, end, depth, windows, doors, radiators, attributes, uuid, passages);

    if (wallData?.outerStartPoint) wall.outerStartPoint = safeVec(wallData.outerStartPoint);
    if (wallData?.outerEndPoint) wall.outerEndPoint = safeVec(wallData.outerEndPoint);
    if (wallData?.outerStartPoint2) wall.outerStartPoint2 = safeVec(wallData.outerStartPoint2);
    if (wallData?.outerEndPoint2) wall.outerEndPoint2 = safeVec(wallData.outerEndPoint2);

    wall.sideALength = wallData?.sideALength;
    wall.sideBLength = wallData?.sideBLength;

    return wall;
  }

  if (wallData?.type === EWall.PARTITION) {
    const { doors, windows, radiators, passages } = parseChildren(wallData);

    const wall = new PartitionWallModel(
      start,
      end,
      depth,
      windows,
      doors,
      radiators,
      attributes,
      uuid,
      passages,
    );

    if (typeof wallData?.isOutOfBounds === 'boolean') {
      wall.setIsOutOfBounds(wallData.isOutOfBounds);
    }

    wall.updateWallLengths?.();
    return wall;
  }

  throw new Error(`Unknown wall type: ${wallData?.type}`);
}
